import type { DietitianProfile, HealthCondition } from '../types/referral';

/**
 * Utility class for dietitian data operations
 * Provides methods for filtering, searching, and extracting data from dietitian profiles
 */
export class DietitianUtils {
  /**
   * Get unique specialties from all dietitians for filter options
   */
  static getUniqueSpecialties(dietitians: DietitianProfile[]): HealthCondition[] {
    const specialties = new Set<HealthCondition>();
    dietitians.forEach(dietitian => {
      dietitian.specialties.forEach(specialty => specialties.add(specialty));
    });
    return Array.from(specialties).sort();
  }

  /**
   * Get unique locations from all dietitians for filter options
   */
  static getUniqueLocations(dietitians: DietitianProfile[]): string[] {
    const locations = new Set<string>();
    dietitians.forEach(dietitian => {
      locations.add(dietitian.location);
    });
    return Array.from(locations).sort();
  }

  /**
   * Filter dietitians based on search criteria
   */
  static filterDietitians(
    dietitians: DietitianProfile[],
    search: string,
    specialty: string,
    location: string
  ): DietitianProfile[] {
    return dietitians.filter(dietitian => {
      // Search filter (name, credentials, bio)
      const searchMatch = !search ||
        dietitian.name.toLowerCase().includes(search.toLowerCase()) ||
        dietitian.credentials.toLowerCase().includes(search.toLowerCase()) ||
        dietitian.bio.toLowerCase().includes(search.toLowerCase()) ||
        dietitian.specialties.some(s => s.toLowerCase().includes(search.toLowerCase()));

      // Specialty filter
      const specialtyMatch = !specialty ||
        dietitian.specialties.includes(specialty as HealthCondition);

      // Location filter (text-based search)
      const locationMatch = !location ||
        dietitian.location.toLowerCase().includes(location.toLowerCase());

      return searchMatch && specialtyMatch && locationMatch;
    });
  }
}